import { Building2, ChevronRight, MapPin } from 'lucide-react';
import React from 'react';

import { interceptLinkClick } from '../../../../lib/linkClick';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../common/button/Button';
import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';

export interface HubListingCardProps {
  name: string;
  /** "34 Boulevard du Port" */
  street: string;
  /** "95000 Cergy" */
  locality: string;
  coverImageUrl?: string | null;
  /** The tag naming what this result is: "Hub". */
  kindLabel: string;
  /** What is held there, under the name: "2 activités ici". */
  detail?: string;
  /** The expanded card's button: "Voir le Hub". */
  viewHubText: string;
  /** The Hub's page, locale prefixed: a real link for crawlers and new tabs. */
  href?: string;
  isSelected?: boolean;
  isHovered?: boolean;
  /** Selects the card (it expands), as a click on an activity card does. */
  onClick?: () => void;
  onHover?: () => void;
  onLeaveHover?: () => void;
  /** Opens the Hub's page. */
  onViewHub?: () => void;
  className?: string;
}

function CoverFallback({ className }: { className?: string }) {
  return (
    <span className={cn('flex h-full w-full items-center justify-center bg-primary-50 text-primary-600', className)}>
      <Building2 className="size-8" strokeWidth={1.5} aria-hidden />
    </span>
  );
}

function KindTag({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md bg-primary-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700',
        className
      )}
    >
      <Building2 className="size-3" aria-hidden />
      {label}
    </span>
  );
}

/**
 * A Hub among the search results: laid out like an activity's ListingCard, compact until selected,
 * then expanded with its cover and a button to its page.
 */
export const HubListingCard = React.memo<HubListingCardProps>(
  ({
    name,
    street,
    locality,
    coverImageUrl,
    kindLabel,
    detail,
    viewHubText,
    href,
    isSelected = false,
    isHovered = false,
    onClick,
    onHover,
    onLeaveHover,
    onViewHub,
    className
  }) => {
    const compact = (
      <div className="relative flex w-full h-[92px] md:h-[112px] cursor-pointer" onClick={href ? undefined : onClick}>
        <div className="h-full aspect-square bg-gray-200 flex-shrink-0 relative overflow-hidden rounded-l-xl">
          <ImageWithFallback
            src={coverImageUrl ?? undefined}
            alt={name}
            className="h-full w-full object-cover"
            sizes="112px"
            fallback={<CoverFallback />}
          />
        </div>
        <div className="flex flex-col p-3 md:px-4 md:py-3 flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-semibold text-neutral-800 truncate leading-snug">
            {href ? (
              <a
                href={href}
                draggable={false}
                onClick={(e) => interceptLinkClick(e, onClick)}
                className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary-500"
              >
                {name}
              </a>
            ) : (
              name
            )}
          </h3>
          <p className="flex items-center gap-2 min-w-0 text-xs md:text-sm font-medium text-neutral-600">
            <KindTag label={kindLabel} className="flex-shrink-0" />
            {detail && <span className="truncate">{detail}</span>}
          </p>
          <div className="mt-auto flex items-center gap-1 min-w-0">
            <MapPin className="w-4 h-4 flex-shrink-0 text-neutral-600" aria-hidden />
            <span className="text-xs md:text-sm font-medium truncate text-neutral-600">{locality}</span>
          </div>
        </div>
      </div>
    );

    const heroContent = (
      <>
        <div className="absolute inset-0">
          <ImageWithFallback
            src={coverImageUrl ?? undefined}
            alt={href ? '' : name}
            className="w-full h-full object-cover"
            sizes="100vw"
            fallback={<CoverFallback />}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/70" />
        </div>
        <div className="relative h-full p-4 flex flex-col justify-between">
          <KindTag label={kindLabel} className="self-start bg-white/90" />
          <div>
            <div className="flex items-center text-left">
              <h1 className="text-2xl font-bold text-white/90 group-hover:text-white transition-colors duration-200">
                {name}
              </h1>
              <ChevronRight
                size={32}
                className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200"
                aria-hidden
              />
            </div>
            {detail && <p className="text-base">{detail}</p>}
          </div>
        </div>
      </>
    );

    const expanded = (
      <>
        {href ? (
          <a
            href={href}
            draggable={false}
            onClick={(e) => interceptLinkClick(e, onViewHub)}
            className="group relative block h-[200px] text-white overflow-hidden cursor-pointer"
          >
            {heroContent}
          </a>
        ) : (
          <div
            className="group relative h-[200px] text-white overflow-hidden cursor-pointer"
            onClick={onViewHub}
            role="button"
            tabIndex={0}
          >
            {heroContent}
          </div>
        )}
        <div className="p-4 flex items-center gap-1 min-w-0">
          <MapPin className="w-4 h-4 text-neutral-600 flex-shrink-0" aria-hidden />
          <span className="text-sm truncate text-neutral-600">
            {street}, {locality}
          </span>
        </div>
        <div className="p-4 mt-auto bg-white border-t border-neutral-200 flex">
          <Button variant="primary" label={viewHubText} onClick={onViewHub} className="flex-1 text-sm md:text-base" />
        </div>
      </>
    );

    return (
      <div
        className={cn(
          'flex flex-col border overflow-hidden w-full bg-white',
          isSelected
            ? 'rounded-2xl border-primary-400 border-2 shadow-lg h-auto'
            : cn(
                'rounded-xl border-neutral-200 md:h-[112px] hover:border-primary-500 hover:shadow-md',
                isHovered && 'border-primary-500 shadow-md'
              ),
          className
        )}
        onClick={!isSelected && !href ? onClick : undefined}
        onMouseEnter={onHover}
        onMouseLeave={onLeaveHover}
        role={onClick && !href ? 'button' : undefined}
        tabIndex={onClick && !href ? 0 : undefined}
      >
        {isSelected ? expanded : compact}
      </div>
    );
  }
);

HubListingCard.displayName = 'HubListingCard';
